import { importLibrary, setOptions } from '@googlemaps/js-api-loader'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { orange } from '@/theme/colors'
import { NumberedMappedEvent } from '../types'

interface MapCanvasProps {
	events: NumberedMappedEvent[]
	selectedEventId: number | null
	onMarkerClick: (eventId: number) => void
}

const MapRoot = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
`

const MapTarget = styled.div`
	position: absolute;
	inset: 0;
`

const Overlay = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f8f9fa;
	color: #6c757d;
	font-size: 0.95rem;
	z-index: 1;
	pointer-events: none;
`

const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined

let loaderPromise: Promise<void> | null = null

const loadGoogleMaps = (): Promise<void> => {
	if (!apiKey) return Promise.reject(new Error('Missing VITE_GOOGLE_MAPS_API_KEY'))
	if (!loaderPromise) {
		setOptions({ key: apiKey, v: 'weekly' })
		loaderPromise = importLibrary('maps').then(() => undefined)
	}
	return loaderPromise
}

const MARKER_RADIUS = 16

const buildMarkerIcon = (active: boolean): google.maps.Symbol => ({
	path: `M 0,0 m -${MARKER_RADIUS},0 a ${MARKER_RADIUS},${MARKER_RADIUS} 0 1,0 ${MARKER_RADIUS * 2},0 a ${MARKER_RADIUS},${MARKER_RADIUS} 0 1,0 -${MARKER_RADIUS * 2},0`,
	fillColor: active ? '#a03c04' : orange,
	fillOpacity: 1,
	strokeColor: 'white',
	strokeWeight: 3,
	scale: active ? 1.15 : 1,
	anchor: new google.maps.Point(0, 0),
	labelOrigin: new google.maps.Point(0, 0),
})

const MapCanvas: React.FC<MapCanvasProps> = ({ events, selectedEventId, onMarkerClick }) => {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const mapRef = useRef<google.maps.Map | null>(null)
	const markersRef = useRef<Map<number, google.maps.Marker>>(new Map())
	const infoWindowRef = useRef<google.maps.InfoWindow | null>(null)
	const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
	const [errorMsg, setErrorMsg] = useState<string | null>(null)

	useEffect(() => {
		let cancelled = false
		const markers = markersRef.current
		loadGoogleMaps()
			.then(() => {
				if (cancelled || !containerRef.current) return
				mapRef.current = new google.maps.Map(containerRef.current, {
					center: { lat: 0, lng: 0 },
					zoom: 2,
					mapTypeControl: false,
					streetViewControl: false,
					fullscreenControl: false,
				})
				infoWindowRef.current = new google.maps.InfoWindow()
				setStatus('ready')
			})
			.catch(err => {
				if (cancelled) return
				setErrorMsg(err instanceof Error ? err.message : 'Failed to load map')
				setStatus('error')
			})
		return () => {
			cancelled = true
			markers.forEach(marker => marker.setMap(null))
			markers.clear()
			infoWindowRef.current?.close()
			infoWindowRef.current = null
			mapRef.current = null
		}
	}, [])

	useEffect(() => {
		if (status !== 'ready' || !mapRef.current) return
		const map = mapRef.current

		markersRef.current.forEach(marker => marker.setMap(null))
		markersRef.current.clear()

		if (events.length === 0) return

		const bounds = new google.maps.LatLngBounds()
		events.forEach(item => {
			const position = { lat: item.lat, lng: item.lng }
			const isActive = selectedEventId === item.event.id
			const marker = new google.maps.Marker({
				position,
				map,
				title: item.event.name,
				label: {
					text: String(item.order),
					color: '#ffffff',
					fontSize: '13px',
					fontWeight: '700',
					fontFamily: 'system-ui, -apple-system, sans-serif',
				},
				icon: buildMarkerIcon(isActive),
				zIndex: isActive ? 1000 : item.order,
			})
			marker.addListener('click', () => onMarkerClick(item.event.id))
			markersRef.current.set(item.event.id, marker)
			bounds.extend(position)
		})

		if (events.length === 1) {
			map.setCenter({ lat: events[0].lat, lng: events[0].lng })
			map.setZoom(14)
		} else {
			map.fitBounds(bounds, 64)
		}
	}, [events, status, selectedEventId, onMarkerClick])

	useEffect(() => {
		if (status !== 'ready' || !mapRef.current || !infoWindowRef.current) return
		if (selectedEventId == null) {
			infoWindowRef.current.close()
			return
		}
		const marker = markersRef.current.get(selectedEventId)
		const item = events.find(e => e.event.id === selectedEventId)
		if (!marker || !item) return

		const time = item.event.startTime ?? ''
		const address = item.event.location?.fullText ?? ''
		const safeName = escapeHtml(item.event.name)
		const safeAddress = escapeHtml(address)
		const safeTime = escapeHtml(time)
		infoWindowRef.current.setContent(`
			<div style="font-family: inherit; max-width: 240px;">
				<div style="font-weight: 700; font-size: 0.95rem; margin-bottom: 4px; color: #212529;">${safeName}</div>
				${safeTime ? `<div style="font-size: 0.8rem; color: #6c757d; margin-bottom: 2px;">Day ${item.dayIndex + 1} · ${safeTime}</div>` : `<div style="font-size: 0.8rem; color: #6c757d; margin-bottom: 2px;">Day ${item.dayIndex + 1}</div>`}
				${safeAddress ? `<div style="font-size: 0.8rem; color: #868e96;">${safeAddress}</div>` : ''}
			</div>
		`)
		infoWindowRef.current.open({ map: mapRef.current, anchor: marker })
		mapRef.current.panTo(marker.getPosition() as google.maps.LatLng)
	}, [selectedEventId, events, status])

	return (
		<MapRoot>
			<MapTarget ref={containerRef} />
			{status === 'loading' && <Overlay>Loading map…</Overlay>}
			{status === 'error' && <Overlay>Map failed to load{errorMsg ? `: ${errorMsg}` : ''}</Overlay>}
		</MapRoot>
	)
}

const escapeHtml = (s: string): string =>
	s
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')

export default MapCanvas
